import { useState } from "react";
import { Users, Trash2, Link2, Copy, Eye, Pencil } from "lucide-react";
import { toast } from "sonner";

export const ShareCalendar = ({ shares, accent, showShared, onToggleShared, onShare, onUnshare, onPublic }) => {
  const [email, setEmail] = useState("");
  const [canEdit, setCanEdit] = useState(false);

  const publicUrl = shares.public?.token
    ? `${window.location.origin}/calendar/shared/${shares.public.token}`
    : "";

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-panel p-4" data-testid="share-calendar">
      <div className="flex flex-wrap items-center gap-3">
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest" style={{ color: accent }}>
          <Users className="h-3.5 w-3.5" /> Sharing
        </p>
        {shares.shared_with_me?.length > 0 && (
          <button
            data-testid="share-show-theirs"
            onClick={onToggleShared}
            className="rounded-full border px-3.5 py-1.5 text-xs font-semibold"
            style={showShared
              ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
              : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
          >
            {showShared ? "Showing their events" : "Show events shared with me"}
          </button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <input
          data-testid="share-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && email.trim() && onShare(email.trim(), canEdit) && setEmail("")}
          placeholder="Their ChatMiLu email"
          className="min-h-[40px] flex-1 rounded-xl border border-white/10 bg-page px-4 text-sm outline-none placeholder:text-ink-soft"
        />
        <button
          data-testid="share-can-edit"
          onClick={() => setCanEdit((c) => !c)}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-full border px-4 text-xs font-semibold"
          style={canEdit
            ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
            : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
        >
          {canEdit ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {canEdit ? "Can add and edit" : "View only"}
        </button>
        <button
          data-testid="share-add"
          onClick={async () => {
            if (!email.trim()) return;
            const ok = await onShare(email.trim(), canEdit);
            if (ok) setEmail("");
          }}
          className="min-h-[40px] rounded-full px-5 text-xs font-semibold text-page active:scale-95"
          style={{ backgroundColor: accent }}
        >
          Share
        </button>
      </div>

      {shares.shared_with?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="shared-with">
          {shares.shared_with.map((s) => (
            <div key={s.share_id} className="flex flex-wrap items-center gap-2 text-sm">
              <Users className="h-3.5 w-3.5 text-ink-soft" />
              <span>{s.name || s.email}</span>
              <span className="text-xs text-ink-soft">{s.can_edit ? "can add and edit" : "view only"}</span>
              <button
                data-testid={`share-remove-${s.share_id}`}
                onClick={() => onUnshare(s)}
                aria-label="Stop sharing"
                className="ms-auto rounded-full border border-white/10 p-1 text-ink-soft hover:border-destructive/60 hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {shares.shared_with_me?.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="shared-with-me">
          {shares.shared_with_me.map((s) => (
            <p key={s.share_id} className="text-xs text-ink-soft">
              {s.name || s.email} shared their calendar with you{s.can_edit ? " — you can add to it" : ""}.
            </p>
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/5 pt-3">
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
          <Link2 className="h-3.5 w-3.5" /> Read-only link
        </span>
        <button
          data-testid="share-public-toggle"
          onClick={() => onPublic(!shares.public?.on)}
          className="rounded-full border px-3.5 py-1.5 text-xs font-semibold"
          style={shares.public?.on
            ? { borderColor: accent, color: accent, backgroundColor: `${accent}22` }
            : { borderColor: "rgba(255,255,255,.12)", color: "#9AA3B2" }}
        >
          {shares.public?.on ? "On" : "Off"}
        </button>
        {publicUrl && (
          <>
            <span className="max-w-full truncate text-xs text-ink-soft" data-testid="share-public-url">{publicUrl}</span>
            <button
              data-testid="share-public-copy"
              onClick={() => {
                navigator.clipboard?.writeText(publicUrl);
                toast.success("Link copied");
              }}
              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-1 text-xs text-ink-soft hover:text-ink"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </>
        )}
      </div>
    </div>
  );
};
